/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import api from '../utils/api';
import { Brand } from '../../../shared/model/brand.model';
import { FindingSuggestionsForPriority } from '../model/assessmentDetail.model';
import {
  convertFiltersInQueryParams,
  emptyFindingSuggestionsFilters,
  FindingSuggestionsFilters,
} from '../components/prioritySelectionHelper/findingSuggestionsQueryParams';
import type { AppThunk } from '../../../core/store';

interface FindingSuggestionsForPrioritySliceState {
  data: FindingSuggestionsForPriority[];
  findingSuggestionsFilters: FindingSuggestionsFilters;
  brands: Brand[];
  regions: string[];
  countries: string[];
  showResults: boolean;
  isFetching: boolean;
  isFetchingCountries: boolean;
  isFetchingRegions: boolean;
  error: string;
}

export const initialState: FindingSuggestionsForPrioritySliceState = {
  data: [],
  findingSuggestionsFilters: emptyFindingSuggestionsFilters,
  brands: [],
  regions: [],
  countries: [],
  showResults: true,
  isFetching: false,
  isFetchingCountries: false,
  isFetchingRegions: false,
  error: '',
};

const findingSuggestionsSlice = createSlice({
  name: 'findingSuggestions',
  initialState,
  reducers: {
    startFetch: (state: Draft<FindingSuggestionsForPrioritySliceState>) => ({ ...state, isFetching: true }),
    startFetchRegions: (state: Draft<FindingSuggestionsForPrioritySliceState>) => ({
      ...state,
      isFetchingRegions: true,
    }),
    startFetchCountries: (state: Draft<FindingSuggestionsForPrioritySliceState>) => ({
      ...state,
      isFetchingCountries: true,
    }),
    finishFetch: (
      state: Draft<FindingSuggestionsForPrioritySliceState>,
      { payload }: PayloadAction<FindingSuggestionsForPriority[]>
    ) => {
      return {
        ...state,
        isFetching: false,
        data: [...payload],
        error: '',
      };
    },
    setFindingSuggestionsFilters: (
      state: Draft<FindingSuggestionsForPrioritySliceState>,
      { payload }: PayloadAction<FindingSuggestionsFilters>
    ) => ({
      ...state,
      findingSuggestionsFilters: payload,
    }),
    setShowResults: (state: Draft<FindingSuggestionsForPrioritySliceState>, { payload }: PayloadAction<boolean>) => ({
      ...state,
      showResults: payload,
    }),
    changeFindingSuggestions: (
      state: Draft<FindingSuggestionsForPrioritySliceState>,
      { payload }: PayloadAction<FindingSuggestionsForPriority[]>
    ) => ({
      ...state,
      data: payload,
    }),
    changeBrands: (state: Draft<FindingSuggestionsForPrioritySliceState>, { payload }: PayloadAction<Brand[]>) => ({
      ...state,
      brands: payload,
    }),
    finishRegionFetch: (state: Draft<FindingSuggestionsForPrioritySliceState>, action: PayloadAction<string[]>) => ({
      ...state,
      isFetchingRegions: false,
      regions: [...action.payload],
      error: '',
    }),
    finishCountriesFetch: (state: Draft<FindingSuggestionsForPrioritySliceState>, action: PayloadAction<string[]>) => ({
      ...state,
      isFetchingCountries: false,
      countries: [...action.payload],
      error: '',
    }),
    errorFetch: (state: Draft<FindingSuggestionsForPrioritySliceState>, { payload }: PayloadAction<string>) => ({
      ...state,
      isFetching: false,
      error: payload,
    }),
    errorRegionsFetch: (state: Draft<FindingSuggestionsForPrioritySliceState>, { payload }: PayloadAction<string>) => ({
      ...state,
      isFetchingRegions: false,
      error: payload,
    }),
    errorCountriesFetch: (
      state: Draft<FindingSuggestionsForPrioritySliceState>,
      { payload }: PayloadAction<string>
    ) => ({
      ...state,
      isFetchingCountries: false,
      error: payload,
    }),
  },
});
export const {
  startFetch,
  finishFetch,
  finishRegionFetch,
  finishCountriesFetch,
  errorFetch,
  changeFindingSuggestions,
  setShowResults,
  changeBrands,
  setFindingSuggestionsFilters,
  startFetchCountries,
  startFetchRegions,
  errorRegionsFetch,
  errorCountriesFetch,
} = findingSuggestionsSlice.actions;

export const fetchFindingSuggestionsForPriority =
  (filters: FindingSuggestionsFilters, processCode?: string, subProcessCode?: string) =>
  async (dispatch: any, state: any) => {
    if (!state().findingSuggestions.isFetching) {
      dispatch(startFetch());
      dispatch(setFindingSuggestionsFilters(filters));
      try {
        const findingSuggestions = await api.getFindingSuggestionsForPriority(
          convertFiltersInQueryParams(filters),
          processCode,
          subProcessCode
        );
        dispatch(finishFetch(findingSuggestions));
      } catch (error) {
        dispatch(errorFetch(JSON.stringify(error)));
      }
    }
  };

export const fetchRegions =
  (filters: FindingSuggestionsFilters): AppThunk =>
  async (dispatch: any) => {
    dispatch(startFetchRegions());
    try {
      const regions: string[] = await api.getRegions(convertFiltersInQueryParams(filters));
      dispatch(finishRegionFetch(regions));
    } catch (error) {
      dispatch(errorRegionsFetch(JSON.stringify(error)));
    }
  };

export const fetchCountries =
  (filters: FindingSuggestionsFilters): AppThunk =>
  async (dispatch: any) => {
    dispatch(startFetchCountries());
    try {
      const countries: string[] = await api.getCountries(convertFiltersInQueryParams(filters));
      dispatch(finishCountriesFetch(countries));
    } catch (error) {
      dispatch(errorCountriesFetch(JSON.stringify(error)));
    }
  };

export const selectFindingSuggestionsForPriority = (state: any) =>
  state.findingSuggestions.data as FindingSuggestionsForPriority[];

export const selectFindingSuggestionsFilters = (state: any) =>
  state.findingSuggestions.findingSuggestionsFilters as FindingSuggestionsFilters;

export const selectBrands = (state: any) => state.findingSuggestions.brands as Brand[];
export const selectRegions = (state: any) => state.findingSuggestions.regions as string[];
export const selectCountries = (state: any) => state.findingSuggestions.countries as string[];

export const selectShowResults = (state: any) => state.findingSuggestions.showResults as boolean;

export const selectIsFetching = (state: any) => state.findingSuggestions.isFetching;
export const selectIsFetchingRegions = (state: any) => state.findingSuggestions.isFetchingRegions;
export const selectIsFetchingCountries = (state: any) => state.findingSuggestions.isFetchingCountries;

export default findingSuggestionsSlice.reducer;
